import { useState, useRef, useEffect, useCallback } from 'react';
import warn from '../utils/warn.js';

/**
 * Inspired by Fluent, Radix, and Spectrum:
 *
 * @see https://github.com/microsoft/fluentui/blob/33ab4d72367ca8a5e1ac13968286f8cda375808a/packages/react-components/react-utilities/src/hooks/useControllableState.ts
 * @see https://github.com/radix-ui/primitives/blob/c31c97274ff357aea99afe6c01c1c8c58b6356e0/packages/react/use-controllable-state/src/useControllableState.tsx
 * @see https://github.com/adobe/react-spectrum/blob/f040ff62678e6a31375b96c05396df0bae660350/packages/%40react-stately/utils/src/useControlledState.ts
 */
/**
 * @see https://github.com/DefinitelyTyped/DefinitelyTyped/blob/3c13388e265ff3f73a8ced988253bfb1a0424c1e/types/react/index.d.ts#L1775
 */
const useControllableState = options => {
  const {
    defaultState,
    initialState,
    state
  } = options;
  const [uncontrolledState, setUncontrolledState] = useState(defaultState ?? initialState);
  const isControlled = state !== undefined;
  const isControlledRef = useRef(state !== undefined);
  useEffect(() => {
    /**
     * Logs warning when switching between controlled and uncontrolled use
     * cases on subsequent renders.
     */
    const wasControlled = isControlledRef.current;
    if (wasControlled !== isControlled) {
      warn(`[IFL] A component changed from ${wasControlled ? "controlled" : "uncontrolled"} to ${isControlled ? "controlled" : "uncontrolled"}`);
    }
    isControlledRef.current = isControlled;
  }, [isControlled]);
  const controlledStateRef = useRef(state);
  useEffect(() => {
    controlledStateRef.current = state;
  }, [state]);
  const setControlledState = useCallback(newState => {
    if (isFactoryDispatch(newState)) {
      newState(controlledStateRef.current);
    }
  }, []);
  return isControlled ? [state, setControlledState] : [uncontrolledState, setUncontrolledState];
};
function isFactoryDispatch(newState) {
  return typeof newState === "function";
}

export { useControllableState };
